import { NgModule } from '@angular/core';
import { NoPreloading, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './services/user/auth.guard';
import { CanLeaveGuard } from './guards/can-leave.guard';
import { FeatureFlagGuard } from './guards/feature-flag.guard';
import {
  CONNECTIONS_ROUTE_GATES,
  CONNECTIONS_ROUTE_DATA,
} from './connections/connections-route-gates';
import { ConnectAccountsAccessGuard } from './guards/connect-accounts-access.guard';
import { FEATURE_FLAGS } from './services/posthog.service';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'login',
    loadChildren: () => import('./login/login.module').then( m => m.LoginPageModule)
  },
  {
    path: 'bookings',
    loadChildren: () => import('./bookings/bookings.module').then( m => m.BookingsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'clients',
    loadChildren: () => import('./clients/clients.module').then( m => m.ClientsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'requests',
    loadChildren: () => import('./requests/requests.module').then( m => m.RequestsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'waitlist',
    loadChildren: () => import('./waitlist/waitlist.module').then( m => m.WaitlistPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'sales',
    loadChildren: () => import('./sales/sales.module').then( m => m.SalesPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'register-closures',
    loadChildren: () => import('./register-closures/register-closures.module').then( m => m.RegisterClosuresPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'products',
    loadChildren: () => import('./products/products.module').then( m => m.ProductsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'inventory',
    loadChildren: () => import('./inventory/inventory.module').then( m => m.InventoryPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'marketing',
    loadChildren: () => import('./marketing/marketing.module').then( m => m.MarketingPageModule),
    canActivate: [AuthGuard],
    canDeactivate: [CanLeaveGuard]
  },
  {
    path: 'reports',
    loadChildren: () => import('./reports/reports.module').then( m => m.ReportsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'settings',
    loadChildren: () => import('./settings/settings.module').then( m => m.SettingsPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'calendar-sync',
    loadChildren: () => import('./calendar-sync/calendar-sync.module').then( m => m.CalendarSyncPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'help',
    loadChildren: () => import('./help/help.module').then( m => m.HelpPageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'register',
    loadChildren: () => import('./register/register.module').then( m => m.RegisterPageModule)
  },
  {
    path: 'password-reset',
    loadChildren: () => import('./password-reset/password-reset.module').then( m => m.PasswordResetPageModule)
  },
  {
    path: 'password-reset-password/:token',
    loadChildren: () => import('./password-reset-password/password-reset-password.module').then( m => m.PasswordResetPasswordPageModule)
  },
  {
    path: 'import-contacts',
    loadChildren: () => import('./import-contacts/contact-list/contact-list.module').then( m => m.ContactListPageModule),
    canActivate: [AuthGuard]

  },
  {
    path: 'online-bookings',
    loadChildren: () => import('./online-bookings/online-booking-page/online-booking-page.module').then( m => m.OnlineBookingPagePageModule),
    canActivate: [AuthGuard]
  },
  {
    path: 'activity',
    loadChildren: () => import('./activity/activity.module').then( m => m.ActivityPageModule)
  },
  {
    path: 'brands',
    loadChildren: () => import('./brands/brands.module').then( m => m.BrandsPageModule)
  },
  {
    path: 'suppliers',
    loadChildren: () => import('./suppliers/suppliers.module').then( m => m.SuppliersPageModule)
  },
  {
    path: 'categories',
    loadChildren: () => import('./categories/categories.module').then( m => m.CategoriesPageModule)
  },
  {
    path: 'text-marketing',
    loadChildren: () => import('./text-marketing/text-marketing.module').then( m => m.TextMarketingPageModule),
    canActivate: [AuthGuard, FeatureFlagGuard],
    data: { featureFlag: FEATURE_FLAGS.bulkText }
  },
  {
    path: 'inventory-count',
    loadChildren: () => import('./inventory-count/inventory-count.module').then( m => m.InventoryCountPageModule)
  },
  {
    path: 'inventory-history',
    loadChildren: () => import('./inventory-history/inventory-history.module').then( m => m.InventoryHistoryPageModule)
  },
  {
    path: 'connections',
    loadChildren: () => import('./connections/connections.module').then( m => m.ConnectionsPageModule),
    // AuthGuard first: a logged-out deep link must land on /login with a
    // returnUrl (as every other authenticated route does) before the
    // salon-targeted flag is evaluated for an identity that doesn't exist yet.
    //
    // Then two independent gates, in this order and both failing closed.
    // FeatureFlagGuard is the ROLLOUT layer (is this salon switched on);
    // ConnectAccountsAccessGuard is the AUTHORISATION layer (may this stylist
    // take part). The flag stays after the permission shipped — neither
    // replaces the other, and either one alone must be able to block.
    // Permission last because it is the one that may present a notice: no
    // point explaining a revoked permission to a salon that never had the
    // feature switched on in the first place.
    // Declared in connections-route-gates.ts so a spec can assert them —
    // importing this module into a test pulls every loadChildren chunk into
    // Karma and exhausts the heap, which is how the guard came to be
    // detachable with no test noticing.
    canActivate: CONNECTIONS_ROUTE_GATES,
    data: CONNECTIONS_ROUTE_DATA
  },


];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { preloadingStrategy: NoPreloading })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
